'use client'

import { useState, useRef, useEffect } from 'react'
import { cn } from '@/utils/tailwind-utils'

// Define available colors based on the app's color system, limited to darker colors for white text readability
const DEFAULT_COLORS = [
  '#15803d', // Dark green
  '#0f766e', // Dark teal
  '#0369a1', // Dark blue
  '#6d28d9', // Dark purple
  '#a21caf', // Dark magenta
  '#b91c1c', // Dark red
  '#92400e', // Dark orange
  '#3f3f46', // Dark gray
]

export interface ColorPickerProps {
  value: string
  onChange: (color: string) => void
  className?: string
  buttonClassName?: string
  popoverClassName?: string
  disabled?: boolean
  showHexInput?: boolean
  colors?: string[]
  title?: string
}

export function ColorPicker({
  value,
  onChange,
  className,
  buttonClassName,
  popoverClassName,
  disabled = false,
  showHexInput = true,
  colors = DEFAULT_COLORS,
  title = 'Select a color',
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleColorClick = (color: string) => {
    onChange(color)
    setIsOpen(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)

    // If it's a valid hex color, update the value
    if (/^#([0-9A-F]{3}){1,2}$/i.test(newValue)) {
      onChange(newValue)
    }
  }

  return (
    <div className={cn('relative inline-block', className)} ref={containerRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center justify-center p-2 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-primary/50',
          disabled && 'opacity-50 cursor-not-allowed',
          buttonClassName
        )}
        title={title}
        aria-label={title}
      >
        <div className="w-6 h-6 rounded-sm" style={{ backgroundColor: value }} aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 p-3 bg-background border border-border rounded-md shadow-lg',
            'left-full top-0 ml-2',
            'max-w-[280px] min-w-[180px]',
            popoverClassName
          )}
        >
          <div className="grid grid-cols-4 gap-2 mb-2">
            {colors.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorClick(color)}
                className={cn(
                  'w-8 h-8 rounded-sm border border-border focus:outline-none focus:ring-2 focus:ring-primary/50',
                  color === value && 'ring-2 ring-white'
                )}
                style={{ backgroundColor: color }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          {showHexInput && (
            <div className="mt-2">
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-sm bg-card border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="#000000"
                maxLength={7}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
